import * as React from 'react';
import Paper from 'src/components/Paper';
import {
  PurchaseCreatePayload,
  PurchaseUploadPayload,
  useCreatePurchaseMutation,
  useDeletePurchaseMutation,
  useGetPurchasesQuery,
  useUpdatePurchaseMutation,
} from 'src/redux/services/purchases';
import { Purchase } from 'src/dto/Credits';
import { PurchasesList } from 'src/pages/BillingAndPlan/components/PaymentHistory/PurchasesList';
import { PurchaseCreateModal } from 'src/pages/BillingAndPlan/components/PaymentHistory/PurchasesList/PurchaseCreateModal';
import { Box, Typography, styled } from '@mui/material';
import { useGetAppsQuery } from 'src/redux/services/apps';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import { useLazyGetAvailableBudgetQuery } from 'src/redux/services/credits';
import { toggleForceLoading } from 'src/redux/slices/creditSlice';
import { useDispatch } from 'react-redux';
import ButtonComponent from 'src/components/ButtonComponent';
import Row from 'src/frontend-common/components/Row';
import PurchasesListSkeleton from 'src/pages/BillingAndPlan/components/PaymentHistory/PurchasesList/Skeleton';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import { PurchasesList as PurchasesListDto } from 'src/redux/services/purchases';
import { fromTimezonedDateToUtcString } from 'src/utils/date';

const Wrapper = styled(Box)(() => ({
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const PaymentHistory: React.FC<{ appKey: string }> = ({ appKey }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [selectedPurchase, setSelectedPurchase] = React.useState<Purchase | null>(null);
  const [purchasesListLength, setPurchasesListLength] = React.useState<number>(0);
  const [purchasesFilteredByAppKey, setPurchasesFilteredByAppKey] = React.useState<PurchasesListDto | undefined>(undefined);

  const {
    data: appKeys,
    isLoading: isAppKeysLoading,
    isError: isAppKeysError,
  } = useGetAppsQuery({
    max_items: 100,
    start: 0,
    search: null,
  });

  const { data: purchases, isFetching: isFetchingPurchases, isError: isErrorPurchases } = useGetPurchasesQuery();

  const [createPurchase, { isSuccess: createPurchaseIsSuccess, isError: createPurchaseIsError, error: createPurchaseError }] = useCreatePurchaseMutation();

  const [updatePurchase, { isSuccess: updatePurchaseIsSuccess, isError: updatePurchaseIsError, error: updatePurchaseError }] = useUpdatePurchaseMutation();

  const [deletePurchase, { isSuccess: deletePurchaseIsSuccess, isError: deletePurchaseIsError, error: deletePurchaseError }] = useDeletePurchaseMutation();

  const [trigger] = useLazyGetAvailableBudgetQuery();

  const triggerUpdateBalance = () => {
    dispatch(toggleForceLoading());
    return setTimeout(() => trigger(), 5 * 1000);
  };

  const handleEdit = (app: Purchase) => {
    setSelectedPurchase(app);
    setIsModalOpen(true);
  };

  const handleNew = () => {
    setIsModalOpen(true);
    setSelectedPurchase(null);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedPurchase(null);
  };

  const handleUpdate = async (app: PurchaseUploadPayload) => {
    setIsModalOpen(false);
    setSelectedPurchase(null);
    // here activation_date & expiration_date are Dates, since they're coming from the UI
    app.activation_date = fromTimezonedDateToUtcString(app.activation_date as Date);
    app.expiration_date = fromTimezonedDateToUtcString(app.expiration_date as Date);
    await updatePurchase(app);
    triggerUpdateBalance();
  };

  const handleCreate = async (values: PurchaseCreatePayload) => {
    setIsModalOpen(false);
    setSelectedPurchase(null);

    // here activation_date & expiration_date are Dates, since they're coming from the UI
    values.activation_date = fromTimezonedDateToUtcString(values.activation_date as Date);
    values.expiration_date = fromTimezonedDateToUtcString(values.expiration_date as Date);
    await createPurchase(values);
    triggerUpdateBalance();
  };

  const handleDelete = async (purchase: Purchase) => {
    await deletePurchase({ id: purchase.id });
    triggerUpdateBalance();
  };

  React.useEffect(() => {
    if (purchases?.items.length) {
      setPurchasesListLength(purchases.items.length);
      const filteredPurchases = {
        ...purchases,
        items: purchases.items.filter((p) => p.app_key === appKey),
      };
      setPurchasesFilteredByAppKey(filteredPurchases);
    }
  }, [purchases, appKey]);

  const isLoading = isAppKeysLoading || isFetchingPurchases;
  const isError = isAppKeysError || isErrorPurchases;

  return (
    <>
      <Row vAlign="center" hAlign="space-between" style={{ marginBottom: 8, padding: 24 }} testId="payment-tab">
        <Typography variant="h5">Payments</Typography>
        <ButtonComponent text="Add Funds" buttontype="mainaction" onClick={() => handleNew()} />
      </Row>
      <Box>
        <Paper>
          <>
            <Wrapper>
              {isLoading && <PurchasesListSkeleton listLength={purchasesListLength} />}
              {!isLoading && isError && <FailureScreen>An error occurred while fetching the payment history, please retry.</FailureScreen>}
            </Wrapper>
            {!isLoading && !isError && purchases && purchasesFilteredByAppKey && (
              <PurchasesList appKeys={appKeys?.items ?? []} onEdit={handleEdit} onDelete={handleDelete} purchases={purchasesFilteredByAppKey} />
            )}
          </>
        </Paper>
      </Box>
      <PurchaseCreateModal
        open={isModalOpen}
        onClose={handleClose}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        initData={selectedPurchase}
        appKey={appKey}
      />
      <SnackBarCompleteHandler
        isError={createPurchaseIsError}
        isSuccess={createPurchaseIsSuccess}
        successMessage="Found added successfully."
        error={createPurchaseError}
      />
      <SnackBarCompleteHandler
        isError={updatePurchaseIsError}
        isSuccess={updatePurchaseIsSuccess}
        successMessage="Found updated successfully."
        error={updatePurchaseError}
      />
      <SnackBarCompleteHandler
        isError={deletePurchaseIsError}
        isSuccess={deletePurchaseIsSuccess}
        successMessage="Found deleted successfully."
        error={deletePurchaseError}
      />
    </>
  );
};
